import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Info, Search, X } from 'lucide-react';
import { reportsAPI } from '../../../services/api';
import { scrollContentToTop } from '../../../utils/scroll';
import { formatPatientAge, ageInYears } from '../../../utils/age';
import Select from '../../Select';
import DatePicker from '../../DatePicker';
import useReport from '../shared/useReport';
import KpiStrip from '../shared/KpiStrip';
import ReportTable from '../shared/ReportTable';
import ReportPrintLayout from '../shared/ReportPrintLayout';
import PrintButton from '../shared/PrintButton';
import { getPresetRange } from '../shared/DateRangeFilter';
import { fmtMoney, fmtNum } from '../shared/chartTheme';
import { DASH, StatusBadge, col, fmtDate, forPrint, human } from '../shared/profileKit';
import PatientProfileView from '../patientProfile/PatientProfileView';
import ColumnPicker from './ColumnPicker';
import {
  AGE_BANDS, CLINICAL_FILTERS, EMPTY_FILTERS, FAMILY_FILTERS, ID_FILTERS, MONEY_FILTERS,
  REGISTERED_FILTERS, VISIT_FILTERS, activeFilterCount, daysBetween, describeFilters,
  matchesFilters, placeOf,
} from './registerFilters';

// Patient Register: every patient on file, one row each, with their visits, family, and (for
// readers allowed them) money and clinical flags beside their details. Search, filter on any of
// those, sort by any column, export or print what is shown, and open any patient's full profile.
//
// It is the one patient list that opens full. The owner chose that on 2026-10-01 over the rule that
// patient lists show nobody until a search, and paid for it with a permission of its own, so this
// screen is only offered to users who hold it (ReportsManagement) and the server checks it again.

const plural = (n, one, many = `${one}s`) => `${fmtNum(n)} ${Number(n) === 1 ? one : many}`;

const CNIC_OF = { FATHER: "father's", MOTHER: "mother's", GUARDIAN: "guardian's" };

const SEVERITY_TONE = { CRITICAL: 'red', HIGH: 'red', MEDIUM: 'amber', LOW: 'grey' };

const FAMILY_ROLE = { HEAD: 'Head', MEMBER: 'Member' };

/**
 * Age under the name, as the Patients list shows it: amber for an adult, a pulsing green pill for a
 * child so a minor stands out on a long list.
 */
const AgeLine = ({ row }) => {
  const age = formatPatientAge(row);
  if (!age) return null;
  const years = ageInYears(row.dateOfBirth);
  if (years === null || years >= 18) return <div className="text-xs font-medium text-amber-600">{age}</div>;
  return (
    <span
      className="inline-flex items-center gap-1 mt-0.5 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-medium animate-pulse"
      title="Minor"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
      {age}
    </span>
  );
};

/**
 * The CNIC a minor is identified by: a parent's or guardian's, never their own (see the one child,
 * one file rule). For a patient with a CNIC of their own, the guardian's when one is on file.
 */
const guardianCnicOf = (r) => {
  if (r.idCnic && r.idCnicOf && r.idCnicOf !== 'OWN') return { cnic: r.idCnic, of: CNIC_OF[r.idCnicOf] };
  if (r.guardianCnic && r.guardianCnic !== r.cnic) return { cnic: r.guardianCnic, of: CNIC_OF.GUARDIAN };
  return null;
};

/**
 * Every column the register can show. DEFAULT_COLUMNS are always on, in that order; the rest are
 * offered under "More columns", grouped as EXTRA_GROUPS lists them, and appended in the order they
 * are ticked. `needs` names the access the server must have granted for a column to be offered.
 */
const COLUMN_DEFS = {
  mrn: { make: () => col('mrn', 'MRN', 'mono') },
  registeredAt: { make: () => col('registeredAt', 'Registered', 'date', { sortValue: (r) => r.registeredAt || null }) },
  name: {
    make: () =>
      col('name', 'Name', 'wrap', {
        render: (r) => (
          <div>
            <div className="font-medium text-gray-900">{r.name || DASH}</div>
            <AgeLine row={r} />
            {(r.husbandName || r.fatherName) && (
              <div className="text-xs text-gray-500">
                {r.husbandName ? `w/o ${r.husbandName}` : `s/d/o ${r.fatherName}`}
              </div>
            )}
          </div>
        ),
        text: (r) => r.name || '',
        // The age has no column of its own, so the export and the printout carry it with the name.
        csv: (r) => [r.name, formatPatientAge(r)].filter(Boolean).join(', '),
        print: (r) => [r.name, formatPatientAge(r)].filter(Boolean).join(', '),
      }),
  },
  gender: { make: () => col('gender', 'Gender', 'text', { text: (r) => (r.gender ? human(r.gender) : '') }) },
  phone: { make: () => col('phone', 'Phone') },
  cnic: { make: () => col('cnic', 'CNIC', 'mono') },
  guardianCnic: {
    make: () =>
      col((r) => guardianCnicOf(r)?.cnic ?? null, 'Guardian CNIC', 'text', {
        render: (r) => {
          const g = guardianCnicOf(r);
          return g ? (
            <span className="font-mono text-xs">
              {g.cnic}
              <span className="ml-1 font-sans text-gray-500">({g.of})</span>
            </span>
          ) : DASH;
        },
        text: (r) => {
          const g = guardianCnicOf(r);
          return g ? `${g.cnic} (${g.of})` : '';
        },
      }),
  },
  lastVisit: { make: () => col('lastVisit', 'Last Visit', 'date') },

  status: {
    label: 'Status',
    make: () =>
      col('status', 'Status', 'status', {
        render: (r) => (
          <span className="inline-flex items-center gap-1">
            <StatusBadge value={r.status} tone={r.status === 'ACTIVE' ? 'green' : 'red'} />
            {r.archived && <StatusBadge label="Archived" tone="grey" />}
          </span>
        ),
        text: (r) => `${human(r.status)}${r.archived ? ' (archived)' : ''}`,
      }),
  },
  bForm: { label: 'B-Form', make: () => col('bForm', 'B-Form', 'mono') },
  maritalStatus: {
    label: 'Marital Status',
    make: () =>
      col('maritalStatus', 'Marital Status', 'status', {
        render: (r) => human(r.maritalStatus),
        text: (r) => (r.maritalStatus ? human(r.maritalStatus) : ''),
      }),
  },
  area: { label: 'Area', make: () => col('area', 'Area', 'wrap') },
  town: { label: 'Town', make: () => col('town', 'Town', 'wrap') },
  city: { label: 'City', make: () => col('city', 'City', 'wrap') },
  visits: { label: 'Visits', make: () => col('visits', 'Visits', 'number') },
  firstVisit: { label: 'First Visit', make: () => col('firstVisit', 'First Visit', 'date') },
  daysSince: {
    label: 'Days Since Last Visit',
    make: (today) =>
      col((r) => (r.lastVisit ? daysBetween(r.lastVisit, today) : null), 'Days Since', 'number', {
        sortValue: (r) => (r.lastVisit ? daysBetween(r.lastVisit, today) : null),
      }),
  },
  lastDoctor: { label: 'Last Doctor', make: () => col('lastDoctor', 'Last Doctor', 'wrap') },
  cancelledTokens: { label: 'Cancelled Tokens', make: () => col('cancelledTokens', 'Cancelled', 'number') },
  familyRole: {
    label: 'Family',
    make: () =>
      col('familyRole', 'Family', 'text', {
        render: (r) =>
          r.familyRole ? (
            <span>
              {FAMILY_ROLE[r.familyRole]}
              {r.familySize ? <span className="text-gray-500"> of {r.familySize}</span> : null}
            </span>
          ) : DASH,
        text: (r) => (r.familyRole ? `${FAMILY_ROLE[r.familyRole]}${r.familySize ? ` of ${r.familySize}` : ''}` : ''),
      }),
  },
  familyHead: {
    label: 'Family Head',
    make: () => col((r) => (r.familyHeadName ? `${r.familyHeadName} (${r.familyHeadMrn})` : null), 'Family Head', 'wrap'),
  },
  guardian: {
    label: 'Guardian',
    make: () =>
      col(
        (r) => (r.guardianName ? `${r.guardianName}${r.guardianRelationship ? `, ${human(r.guardianRelationship)}` : ''}` : null),
        'Guardian',
        'wrap'
      ),
  },
  guardianPhone: { label: 'Guardian Phone', make: () => col('guardianPhone', 'Guardian Phone') },
  totalPaid: { label: 'Total Paid', needs: 'money', make: () => col('totalPaid', 'Total Paid', 'money') },
  unpaidAmount: {
    label: 'Awaiting Payment',
    needs: 'money',
    make: () =>
      col('unpaidAmount', 'Awaiting Payment', 'money', {
        render: (r) =>
          Number(r.unpaidBills) > 0 ? (
            <span className="text-amber-700 font-medium">
              {fmtMoney(r.unpaidAmount)} <span className="text-xs font-normal">({plural(r.unpaidBills, 'bill')})</span>
            </span>
          ) : DASH,
      }),
  },
  creditBalance: {
    label: 'Credit',
    needs: 'money',
    make: () => col('creditBalance', 'Credit', 'money', { render: (r) => (Number(r.creditBalance) > 0 ? fmtMoney(r.creditBalance) : DASH) }),
  },
  activeAlerts: {
    label: 'Alerts',
    needs: 'clinical',
    make: () =>
      col('activeAlerts', 'Alerts', 'number', {
        render: (r) =>
          Number(r.activeAlerts) > 0 ? (
            <StatusBadge label={`${r.activeAlerts} · ${human(r.worstAlert)}`} tone={SEVERITY_TONE[r.worstAlert] || 'grey'} />
          ) : DASH,
        text: (r) => (Number(r.activeAlerts) > 0 ? `${r.activeAlerts} (${human(r.worstAlert)})` : ''),
      }),
  },
  alertTitles: { label: 'Alert Details', needs: 'clinical', make: () => col('alertTitles', 'Alert Details', 'wrap') },
  medicalHistory: {
    label: 'Medical History',
    needs: 'clinical',
    make: () =>
      col((r) => (r.medicalHistory ? 'Yes' : Number(r.earlierHistories) > 0 ? 'Earlier record only' : 'No'), 'Medical History'),
  },
};

const DEFAULT_COLUMNS = ['mrn', 'registeredAt', 'name', 'gender', 'phone', 'cnic', 'guardianCnic', 'lastVisit'];

const EXTRA_GROUPS = [
  { label: 'Patient', keys: ['status', 'bForm', 'maritalStatus'] },
  { label: 'Address', keys: ['area', 'town', 'city'] },
  { label: 'Visits', keys: ['visits', 'firstVisit', 'daysSince', 'lastDoctor', 'cancelledTokens'] },
  { label: 'Family & guardian', keys: ['familyRole', 'familyHead', 'guardian', 'guardianPhone'] },
  { label: 'Money', keys: ['totalPaid', 'unpaidAmount', 'creditBalance'] },
  { label: 'Clinical', keys: ['activeAlerts', 'alertTitles', 'medicalHistory'] },
];

// The extra columns a reader chose are remembered in this browser, a convenience only: the report
// opens with the default columns wherever storage is unavailable.
const EXTRAS_STORAGE_KEY = 'pamh.patientRegister.extraColumns';
const loadExtras = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(EXTRAS_STORAGE_KEY) || '[]');
    return Array.isArray(saved) ? saved.filter((k) => COLUMN_DEFS[k] && !DEFAULT_COLUMNS.includes(k)) : [];
  } catch {
    return [];
  }
};
const saveExtras = (keys) => {
  try {
    window.localStorage.setItem(EXTRAS_STORAGE_KEY, JSON.stringify(keys));
  } catch {
    // Not remembered; the choice still applies to this visit.
  }
};

/** The default columns, then the chosen extras the server's access allows, each keyed by its id. */
const buildColumns = (extras, access, today) => {
  const offered = (k) => !COLUMN_DEFS[k].needs || access[COLUMN_DEFS[k].needs];
  return [...DEFAULT_COLUMNS, ...extras.filter(offered)].map((k) => ({ ...COLUMN_DEFS[k].make(today), key: k }));
};

const Note = ({ children }) => (
  <div className="flex items-start gap-2 bg-sky-50 border border-sky-200 text-sky-900 rounded-xl px-4 py-3 text-sm">
    <Info className="w-4 h-4 mt-0.5 shrink-0" />
    <div>{children}</div>
  </div>
);

/** A labelled filter dropdown; the first option always clears it. */
const FilterSelect = ({ label, value, onChange, options, anyLabel = 'Any' }) => (
  <div className="min-w-[180px] flex-1">
    <Select label={label} value={value} onChange={onChange} options={[{ value: '', label: anyLabel }, ...options]} />
  </div>
);

const PatientRegisterReport = () => {
  const [includeArchived, setIncludeArchived] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [extras, setExtras] = useState(loadExtras);
  const [openId, setOpenId] = useState(null);
  const listRef = useRef(null);
  const wasOpen = useRef(false);

  const fetcher = useCallback(() => reportsAPI.getPatientRegister({ includeArchived }), [includeArchived]);
  const { data, loading } = useReport(fetcher);

  useEffect(() => {
    if (openId) scrollContentToTop();
    else if (wasOpen.current) listRef.current?.scrollIntoView({ block: 'start' });
    wasOpen.current = Boolean(openId);
  }, [openId]);

  const today = data?.asOf || getPresetRange('today').from;
  const access = data?.access || {};
  const rows = useMemo(() => data?.rows || [], [data]);
  const set = (key) => (value) => setFilters((f) => ({ ...f, [key]: value }));

  // One pass over the rows feeds the KPIs, the table, the CSV and the printout alike, so the
  // numbers above the list always describe exactly the patients in it.
  const shown = useMemo(() => rows.filter((r) => matchesFilters(r, filters, today)), [rows, filters, today]);

  const options = useMemo(() => {
    const distinct = (pick) =>
      [...new Set(rows.map(pick).filter(Boolean))].sort((a, b) => a.localeCompare(b));
    return {
      genders: distinct((r) => r.gender).map((g) => ({ value: g, label: human(g) })),
      statuses: distinct((r) => r.status).map((s) => ({ value: s, label: human(s) })),
      places: distinct(placeOf).map((p) => ({ value: p, label: p })),
    };
  }, [rows]);

  const columns = useMemo(
    () => buildColumns(extras, { money: access.money, clinical: access.clinical }, today),
    [extras, today, access.money, access.clinical]
  );
  const pickerGroups = EXTRA_GROUPS.map((g) => ({
    label: g.label,
    columns: g.keys
      .filter((k) => !COLUMN_DEFS[k].needs || access[COLUMN_DEFS[k].needs])
      .map((k) => ({ key: k, label: COLUMN_DEFS[k].label })),
  })).filter((g) => g.columns.length > 0);
  const chooseExtras = (keys) => {
    setExtras(keys);
    saveExtras(keys);
  };

  const count = activeFilterCount(filters);
  const sum = (key) => shown.reduce((t, r) => t + (Number(r[key]) || 0), 0);
  const kpis = [
    { label: 'Patients', value: fmtNum(shown.length), sub: count ? `of ${fmtNum(rows.length)} on file` : 'On file' },
    {
      label: 'Female / Male',
      value: `${fmtNum(shown.filter((r) => r.gender === 'FEMALE').length)} / ${fmtNum(shown.filter((r) => r.gender === 'MALE').length)}`,
      sub: 'Patients',
    },
    {
      label: 'Children',
      value: fmtNum(shown.filter((r) => { const a = ageInYears(r.dateOfBirth); return a !== null && a < 18; }).length),
      sub: 'Under 18',
    },
    {
      label: 'Seen in 90 Days',
      value: fmtNum(shown.filter((r) => r.lastVisit && daysBetween(r.lastVisit, today) <= 90).length),
      sub: `${plural(shown.filter((r) => Number(r.visits) === 0).length, 'patient')} never visited`,
    },
    ...(access.money
      ? [{ label: 'Total Paid', value: fmtMoney(sum('totalPaid')), sub: sum('unpaidAmount') > 0 ? `${fmtMoney(sum('unpaidAmount'))} awaiting payment` : 'Nothing awaiting payment' }]
      : []),
    ...(access.clinical
      ? [{ label: 'With Alerts', value: fmtNum(shown.filter((r) => Number(r.activeAlerts) > 0).length), sub: `${fmtNum(shown.filter((r) => r.medicalHistory).length)} with medical history` }]
      : []),
  ];

  const placeLabels = Object.fromEntries(options.places.map((p) => [p.value, p.label]));
  const filterText = describeFilters(filters, placeLabels);
  const hiddenNote = [!access.money && 'what each patient paid', !access.clinical && 'medical alerts and history']
    .filter(Boolean)
    .join(' and ');

  return (
    <>
      <div hidden={Boolean(openId)} className="space-y-5">
        <div className="bg-white rounded-2xl shadow p-4 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[260px]">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                id="register-search"
                value={filters.q}
                onChange={(e) => set('q')(e.target.value)}
                placeholder="Search by name, MRN, phone, CNIC, relative or place"
                className="w-full pl-9 pr-9 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              {filters.q && (
                <button type="button" onClick={() => set('q')('')} aria-label="Clear search" className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-400 hover:text-gray-600">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
              <input id="register-archived" type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" />
              Include archived patients
            </label>
            {count > 0 && (
              <button type="button" onClick={() => setFilters(EMPTY_FILTERS)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200">
                <X className="w-4 h-4" /> Clear {plural(count, 'filter')}
              </button>
            )}
            <PrintButton />
          </div>

          <div className="flex flex-wrap gap-3">
            <FilterSelect label="Gender" value={filters.gender} onChange={set('gender')} options={options.genders} />
            <FilterSelect label="Age" value={filters.age} onChange={set('age')} options={AGE_BANDS.map(({ value, label }) => ({ value, label }))} />
            <FilterSelect label="Status" value={filters.status} onChange={set('status')} options={options.statuses} />
            <FilterSelect label="Place" value={filters.place} onChange={set('place')} options={options.places} anyLabel="Anywhere" />
            <FilterSelect label="Visits" value={filters.visits} onChange={set('visits')} options={VISIT_FILTERS} />
            <FilterSelect label="Family" value={filters.family} onChange={set('family')} options={FAMILY_FILTERS} />
            <FilterSelect label="ID documents" value={filters.id} onChange={set('id')} options={ID_FILTERS} />
            {access.clinical && <FilterSelect label="Clinical" value={filters.clinical} onChange={set('clinical')} options={CLINICAL_FILTERS} />}
            {access.money && <FilterSelect label="Money" value={filters.money} onChange={set('money')} options={MONEY_FILTERS} />}
            <FilterSelect label="Registered" value={filters.registered} onChange={set('registered')} options={REGISTERED_FILTERS} anyLabel="Any time" />
          </div>

          {filters.registered === 'custom' && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-600">Registered from</span>
              <DatePicker value={filters.registeredFrom} max={filters.registeredTo || undefined} onChange={set('registeredFrom')} placeholder="From" />
              <span className="text-sm text-gray-400">to</span>
              <DatePicker value={filters.registeredTo} min={filters.registeredFrom || undefined} onChange={set('registeredTo')} placeholder="To" />
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-gray-100">
            <ColumnPicker groups={pickerGroups} chosen={extras} onChange={chooseExtras} />
            <span className="text-xs text-gray-500">
              Shown: MRN, registered, name and age, gender, phone, CNIC, guardian CNIC and last visit
              {extras.length > 0 ? `, plus ${plural(extras.length, 'more column')}` : ''}.
            </span>
          </div>
        </div>

        <KpiStrip items={kpis} loading={loading && !data} />

        {data && hiddenNote && <Note>{hiddenNote.charAt(0).toUpperCase() + hiddenNote.slice(1)} are shown to users with the {[!access.money && 'Finance', !access.clinical && 'Clinical'].filter(Boolean).join(' and ')} reports permission.</Note>}

        <div ref={listRef} className="scroll-mt-4">
          <ReportTable
            title={count ? `${plural(shown.length, 'patient')} of ${fmtNum(rows.length)}` : plural(shown.length, 'patient')}
            columns={columns}
            rows={shown}
            searchable={false}
            pageSize={25}
            onRowClick={(r) => setOpenId(r.id)}
            csvName={`patient-register-${today}`}
            emptyText={loading ? 'Loading' : rows.length ? 'No patient matches these filters' : 'No patients on file'}
            footnote={`${plural(shown.length, 'patient')}${count ? `, filtered by ${filterText}` : ''}. Click a column heading to sort, and a patient to open their full profile. A visit is a token that was not cancelled.`}
          />
        </div>

        <ReportPrintLayout
          title="Patient Register"
          subtitle={`As of ${fmtDate(today)}. Filters: ${filterText}.${includeArchived ? ' Archived patients included.' : ''}`}
          kpis={kpis.map((x) => ({ label: x.label, value: x.value }))}
          tables={[{ title: 'Patients', columns: forPrint(columns), rows: shown }]}
        />
      </div>

      {openId && (
        <PatientProfileView
          key={openId}
          patientId={openId}
          backLabel="Back to patient register"
          onBack={() => setOpenId(null)}
          onOpenPatient={(id) => setOpenId(id)}
        />
      )}
    </>
  );
};

export default PatientRegisterReport;
